import React, { type JSX } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { CustomButton } from './CustomButton-ui';

/**
 * Color info-500 definido en styles.css
 * --color-info-500: #0d6efd
 */
const INFO_COLOR = '#0d6efd';
const INFO_COLOR_OPACITY = 'rgba(13, 110, 253, 0.2)';

/**
 * @interface ConfirmacionDialogProps
 * @description Propiedades del componente ConfirmacionDialog.
 * @property {boolean} open - Indica si el diálogo está abierto.
 * @property {() => void} onClose - Función callback que se ejecuta al cerrar el diálogo.
 * @property {() => void} onConfirm - Función callback que se ejecuta al confirmar.
 * @property {string} [title] - Título del diálogo. Por defecto: "Confirmar Acción".
 * @property {string} [subtitle] - Subtítulo del diálogo. Por defecto: "Verifica los detalles antes de continuar".
 * @property {string | React.ReactNode} message - Mensaje principal de confirmación.
 * @property {string} [confirmText] - Texto del botón de confirmación. Por defecto: "Confirmar".
 * @property {string} [cancelText] - Texto del botón de cancelación. Por defecto: "Cancelar".
 * @property {boolean} [loading] - Indica si está en estado de carga.
 * @property {React.ReactNode} [additionalInfo] - Información adicional a mostrar (opcional).
 * @property {number} [zIndex] - z-index del modal (p. ej. por encima de overlays a pantalla completa del host).
 */
export interface ConfirmacionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  subtitle?: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  additionalInfo?: React.ReactNode;
  zIndex?: number;
}

/**
 * @component ConfirmacionDialog
 * @description Componente de diálogo de confirmación reutilizable con diseño empresarial.
 * Usa el color info-500 (#0d6efd) definido en styles.css.
 * @param {ConfirmacionDialogProps} props - Propiedades del componente.
 * @returns {JSX.Element} El componente renderizado con el diálogo de confirmación.
 */
export const ConfirmacionDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirmar Acción',
  subtitle = 'Verifica los detalles antes de continuar',
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  loading = false,
  additionalInfo,
  zIndex,
}: ConfirmacionDialogProps): JSX.Element => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={zIndex !== undefined ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
          overflow: 'hidden',
          boxShadow:
            '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {/* Header de confirmación */}
      <div className="relative bg-gradient-to-br from-slate-50 via-white to-slate-50 px-8 py-6 border-b border-slate-200/80">
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{
            backgroundColor: INFO_COLOR,
          }}
        ></div>

        <div className="flex items-center gap-5">
          <div className="relative flex-shrink-0">
            <div
              className="absolute inset-0 rounded-2xl blur-xl"
              style={{ backgroundColor: INFO_COLOR_OPACITY }}
            ></div>
            <div
              className="relative p-4 rounded-2xl shadow-lg"
              style={{
                backgroundColor: INFO_COLOR,
                boxShadow: `0 10px 15px -3px ${INFO_COLOR_OPACITY}, 0 4px 6px -2px ${INFO_COLOR_OPACITY}`,
              }}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-white" />
            </div>
          </div>

          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">
              {title}
            </h2>
            <p className="text-sm text-slate-600 mt-1 font-medium">{subtitle}</p>
          </div>
        </div>
      </div>

      <DialogContent sx={{ p: 0, backgroundColor: '#ffffff' }}>
        <div className="px-8 py-6">
          {/* Mensaje de confirmación mejorado */}
          <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
            <div className="text-base text-slate-700 leading-relaxed">
              {typeof message === 'string' ? <p>{message}</p> : message}
            </div>

            {/* Información adicional */}
            {additionalInfo && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                {additionalInfo}
              </div>
            )}
          </div>
        </div>

        {/* Footer de acciones */}
        <div className="px-8 py-5 bg-slate-50/50 border-t border-slate-200 flex justify-end gap-3">
          <CustomButton
            type="button"
            text={cancelText}
            onClick={onClose}
            className="px-7 py-2.5 bg-background-primary text-slate-700 border border-slate-300 rounded-lg text-sm font-semibold shadow-sm hover:shadow-md hover:bg-slate-50 hover:border-slate-400 transition-all duration-200"
          />
          <div
            style={{
              backgroundColor: INFO_COLOR,
            }}
            className="rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CustomButton
              type="button"
              text={confirmText}
              onClick={onConfirm}
              isLoading={loading}
              disabled={loading}
              className="px-7 py-2.5 text-white text-sm font-semibold bg-transparent hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmacionDialog;
